import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection, speakerStatus, SubjectStatus, SubjectType } from 'constants/index'
import { signOut } from 'store/auth'
import { toast } from 'react-toastify'
import { first, chunk } from 'lodash'
import conferenceSlice from 'store/conference'
import { deleteBatchDocuments } from 'utils'
import { deleteImageFromStorageIfExist, placeImageToDifferentPath } from '../utils/subject'
import { nanoid } from 'nanoid'
import { cloudFunctionsInstance } from 'config/axios'

export const initialState = {
  error: null,
  inProgress: true,
  subjects: [],
  confFilter: '',
  subject: null,
  status: '',
  subjectType: SubjectType.EXHIBITOR,
  currentPage: 0,
  pageSize: 10
}

const subjectSlice = createSlice({
  name: 'subject',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setSubjects: (state, action) => {
      state.subjects = action.payload
      state.inProgress = false
    },
    setSubject: (state, action) => {
      state.subject = action.payload
    },
    setSubjectStatus: (state, action) => {
      state.subject.status = action.payload
    },
    setConferenceFilter: (state, action) => {
      state.confFilter = action.payload
    },
    setSubjectStatusFilter: (state, action) => {
      state.status = action.payload
    },
    setSubjectType: (state, action) => {
      state.subjectType = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = true
      state.subjects = []
      state.confFilter = ''
      state.status = ''
      state.subjectType = SubjectType.EXHIBITOR
      state.subject = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const getCollectionNameBySubjectType = (type) => {
  if (type === SubjectType.EXHIBITOR) {
    return Collection.VENDORS
  } else if (type === SubjectType.SPEAKER) {
    return Collection.SPEAKERS
  } else return Collection.SPONSORS
}

export const scanImportedSubjects = async (subjectType, importedSubjects, filteredConferenceId) => {
  const confSnap = await FirestoreDatabase.collection(Collection.CONFERENCES)
    .doc(filteredConferenceId)
    .get()
  const conference = { id: confSnap.id, ...confSnap.data() }
  const linkedConferenceId = conference?.previousConferenceId || ''

  if (linkedConferenceId) {
    const collectionName = getCollectionNameBySubjectType(subjectType)
    const subjectSnap = await FirestoreDatabase.collection(collectionName)
      .where('conferenceId', '==', linkedConferenceId)
      .get()
    const confSubjects = subjectSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }))

    const brandNewSubjects = []
    const existingSubjects = []

    importedSubjects.forEach((obj) => {
      const email = obj.email
      let found = false

      for (let i = 0; i < confSubjects.length; i++) {
        if (confSubjects[i].email === email) {
          found = true
          existingSubjects.push(confSubjects[i])
          break // Exit the loop if email found
        }
      }
      if (!found) {
        brandNewSubjects.push(obj)
      }
    })
    return {
      importedSubjects: importedSubjects,
      brandNewSubjects: brandNewSubjects,
      existingSubjects: existingSubjects
    }
  }
  return {
    importedSubjects: importedSubjects,
    brandNewSubjects: importedSubjects,
    existingSubjects: []
  }
}

const createSpeaker = (s) => {
  const subjectCopy = { ...s }
  subjectCopy?.id && delete subjectCopy['id']
  subjectCopy?.token && delete subjectCopy['token']
  subjectCopy?.type && delete subjectCopy['type']
  return {
    ...subjectCopy,
    id: nanoid(26),
    subjectId: s?.id,
    isSubject: true,
    status: speakerStatus.PENDING
  }
}

export const bulkInsertSubjects = (subjectType, subjects, conferenceId) => async (dispatch) => {
  try {
    const existingEmails = []

    const existingDocsSnapshot = await FirestoreDatabase.collection(Collection.SUBJECTS)
      .where('conferenceId', '==', conferenceId)
      .where('type', '==', subjectType)
      .get()
    existingDocsSnapshot.docs.forEach((doc) => {
      const docData = doc.data()
      docData?.email && existingEmails.push(docData?.email)
    })

    const uniqueSubjects = subjects
      .filter((sub) => !existingEmails.includes(sub.email))
      .map((s) => ({ id: nanoid(24), ...s }))
    const uniqueCount = uniqueSubjects.length
    const existingCount = subjects.length - uniqueCount

    const batchSize = 500 // firebase allows us to commit a batch max 500 documents
    const batchedRecipientsData = chunk(uniqueSubjects, batchSize)
    const batch = FirestoreDatabase.batch()

    const batchedPromises = batchedRecipientsData.map((batchRecipients) => {
      batchRecipients.forEach((subject) => {
        const subjectCopy = { ...subject }
        subjectCopy?.id && delete subjectCopy['id']
        const docRef = FirestoreDatabase.collection(Collection.SUBJECTS).doc(subject.id)
        batch.set(docRef, subjectCopy)
      })
      return batch.commit()
    })

    await Promise.all(batchedPromises)
    if (subjectType === SubjectType.SPEAKER) {
      await createSpeakersImmediatelyAfterSubjectCreation(uniqueSubjects, conferenceId)
    }

    return { uniqueCount, existingCount }
  } catch (err) {
    console.error(err)
    dispatch(subjectSlice.actions.setError(err))
  }
}

export const bulkInsertExistingSubjects = async (subjectType, newSubjects, conferenceId) => {
  try {
    const existingEmails = []

    const existingDocsSnapshot = await FirestoreDatabase.collection(Collection.SUBJECTS)
      .where('conferenceId', '==', conferenceId)
      .where('type', '==', subjectType)
      .get()
    existingDocsSnapshot.docs.forEach((doc) => {
      const docData = doc.data()
      docData?.email && existingEmails.push(docData?.email)
    })

    const previousConferenceId = first(newSubjects)?.conferenceId || ''
    const uniqueSubjects = newSubjects
      .filter((sub) => !existingEmails.includes(sub.email))
      .map((s) => {
        const subjectCopy = { ...s }
        subjectCopy?.booth && delete subjectCopy['booth']
        subjectCopy?.sponsorshipLevelId && delete subjectCopy['sponsorshipLevelId']
        return {
          ...subjectCopy,
          type: subjectType,
          id: nanoid(24),
          approvedAt: null,
          invitedAt: null,
          submittedAt: null,
          updateInfoNotificationSent: false,
          token: nanoid(),
          status: SubjectStatus.PENDING_DATA_REVIEW,
          conferenceId: conferenceId
        }
      })
    const uniqueCount = uniqueSubjects.length
    const existingCount = newSubjects.length - uniqueCount

    const batchSize = 500
    const batchedRecipientsData = chunk(uniqueSubjects, batchSize)
    const batch = FirestoreDatabase.batch()

    const batchedPromises = batchedRecipientsData.map((batchRecipients) => {
      batchRecipients.forEach((subject) => {
        const docRef = FirestoreDatabase.collection(Collection.SUBJECTS).doc(subject?.id)
        const subjectCopy = { ...subject }
        delete subjectCopy['id']
        batch.set(docRef, subjectCopy)
      })
      return batch.commit()
    })
    await Promise.all(batchedPromises)

    const imagePromises = []
    for (const batchRecipients of batchedRecipientsData) {
      for (const subject of batchRecipients) {
        const collectionName = getCollectionNameBySubjectType(subjectType)
        const confPath = `images/${previousConferenceId}/${collectionName}/`
        const path = 'images/subjects/'
        if (subjectType === SubjectType.EXHIBITOR && subject?.logoImage) {
          const sourcePath = `${confPath}${subject?.logoImage}`
          const destinationPath = `${path}${subject?.logoImage}`
          imagePromises.push(placeImageToDifferentPath(sourcePath, destinationPath))
        } else if (subjectType === SubjectType.SPEAKER && subject?.imageName) {
          const sourcePath = `${confPath}${subject?.imageName}`
          const destinationPath = `${path}${subject?.imageName}`
          imagePromises.push(placeImageToDifferentPath(sourcePath, destinationPath))
        } else if (subjectType === SubjectType.SPONSOR) {
          if (subject?.adImageName) {
            imagePromises.push(
              placeImageToDifferentPath(confPath + subject.adImageName, path + subject.adImageName)
            )
          }

          if (subject?.logoImageName) {
            imagePromises.push(
              placeImageToDifferentPath(
                confPath + subject.logoImageName,
                path + subject.logoImageName
              )
            )
          }

          if (subject?.brochureImageName) {
            imagePromises.push(
              placeImageToDifferentPath(
                confPath + subject.brochureImageName,
                path + subject.brochureImageName
              )
            )
          }
        }
      }
    }
    await Promise.all(imagePromises)

    if (subjectType === SubjectType.SPEAKER) {
      await createSpeakersImmediatelyAfterSubjectCreation(uniqueSubjects, conferenceId)
    }

    return {
      uniqueCount: uniqueCount,
      existingCount: existingCount
    }
  } catch (err) {
    console.error(err)
  }
}

export const createSpeakersImmediatelyAfterSubjectCreation = async (subjects, conferenceId) => {
  const batchSize = 500
  // creating new speakers and linking subjects to relevant speakers
  const speakersSnapshot = await FirestoreDatabase.collection(Collection.SPEAKERS)
    .where('conferenceId', '==', conferenceId)
    .get()
  const successfulSpeakers = speakersSnapshot?.docs?.map((doc) => ({
    id: doc.id,
    ...doc.data()
  }))
  const successfulSpeakerEmails = successfulSpeakers?.map((s) => s?.email)

  const newSpeakersToBeCreated = subjects
    ?.filter((s) => !successfulSpeakerEmails?.includes(s?.email))
    .map(createSpeaker)

  const batchedRecipientsData = chunk(newSpeakersToBeCreated, batchSize)
  const batch1 = FirestoreDatabase.batch()

  const batchedSpeakerPromises = batchedRecipientsData.map((batchRecipients) => {
    batchRecipients.forEach((speaker) => {
      const docRef = FirestoreDatabase.collection(Collection.SPEAKERS).doc(speaker?.id)
      const speakerCopy = { ...speaker }
      speakerCopy?.id && delete speakerCopy['id']
      batch1.set(docRef, speakerCopy)
    })
    return batch1.commit()
  })

  await Promise.all(batchedSpeakerPromises)

  // linking speakers to relevant subjects
  const batchedSpeakersData = chunk(newSpeakersToBeCreated, batchSize)
  const batch2 = FirestoreDatabase.batch()

  const batchedSubjectsPromises = batchedSpeakersData.map((batchSpeakers) => {
    batchSpeakers.forEach((speaker) => {
      const docRef = FirestoreDatabase.collection(Collection.SUBJECTS).doc(speaker?.subjectId)
      const speakerCopy = { ...speaker }
      speakerCopy?.id && delete speakerCopy['id']
      speakerCopy?.isSubject && delete speakerCopy['isSubject']
      speakerCopy?.subjectId && delete speakerCopy['subjectId']
      speakerCopy?.status && delete speakerCopy['status']
      batch2.update(docRef, {
        ...speakerCopy,
        speakerId: speaker?.id
      })
    })
    return batch2.commit()
  })

  await Promise.all(batchedSubjectsPromises)
}

export const createSubject = (payload) => async (dispatch) => {
  try {
    dispatch(subjectSlice.actions.setProgress(true))
    if (payload.type === SubjectType.ATTENDEE) {
      const existingDoc = await FirestoreDatabase.collection(Collection.SUBJECTS)
        .where('email', '==', payload.email)
        .where('conferenceId', '==', payload.conferenceId)
        .where('type', '==', payload.type)
        .limit(1)
        .get()
      if (!existingDoc.docs.length) {
        const response = await FirestoreDatabase.collection(Collection.SUBJECTS).add(payload)
        return response.id
      } else {
        toast.error('Attendee subject with this email already exists.')
        return null
      }
    } else {
      const existingDoc = await FirestoreDatabase.collection(Collection.SUBJECTS)
        .where('email', '==', payload.email)
        .where('conferenceId', '==', payload.conferenceId)
        .where('type', '==', payload.type)
        .limit(1)
        .get()

      if (!existingDoc.docs.length) {
        const response = await FirestoreDatabase.collection(Collection.SUBJECTS).add(payload)
        return response.id
      } else {
        toast.error('Subject with this email already exists.')
        return null
      }
    }
  } catch (err) {
    console.error(err)
    dispatch(subjectSlice.actions.setError(err))
  } finally {
    dispatch(subjectSlice.actions.setProgress(false))
  }
}

export const fetchSubjectById = (subjectId) => async (dispatch) => {
  try {
    dispatch(subjectSlice.actions.setProgress(true))
    const response = await FirestoreDatabase.collection(Collection.SUBJECTS).doc(subjectId).get()
    dispatch(
      subjectSlice.actions.setSubject({
        id: response.id,
        ...response.data()
      })
    )
  } catch (err) {
    dispatch(subjectSlice.actions.setError(err))
  } finally {
    dispatch(subjectSlice.actions.setProgress(false))
  }
}

export const fetchSubjectByToken = (token) => async (dispatch) => {
  try {
    dispatch(subjectSlice.actions.setProgress(true))
    const response = await FirestoreDatabase.collection(Collection.SUBJECTS)
      .where('token', '==', token)
      .limit(1)
      .get()
    const subject = response.docs.length > 0 ? first(response.docs) : null
    if (subject) {
      dispatch(
        subjectSlice.actions.setSubject({
          id: subject.id,
          ...subject.data()
        })
      )
    }
  } catch (err) {
    dispatch(subjectSlice.actions.setError(err))
  } finally {
    dispatch(subjectSlice.actions.setProgress(false))
  }
}

export const updateSubjectById =
  ({ showSuccessMsg = true }, subjectId, payload, conferenceId) =>
  async (dispatch) => {
    try {
      const subjectSnap = await FirestoreDatabase.collection(Collection.SUBJECTS)
        .doc(subjectId)
        .get()
      const subject = { ...subjectSnap.data(), id: subjectSnap.id }

      const existingEmails = []

      const existingDocsSnapshot = await FirestoreDatabase.collection(Collection.SUBJECTS)
        .where('conferenceId', '==', conferenceId)
        .where('type', '==', subject.type)
        .get()

      const updatedSnapshot = existingDocsSnapshot.docs.filter((doc) => doc.id !== subjectId)

      updatedSnapshot.forEach((doc) => {
        const docData = doc.data()
        existingEmails.push(docData.email)
      })

      if (existingEmails.includes(payload.email)) {
        toast.error(
          `${
            subject.type === SubjectType.ATTENDEE || subject.type === SubjectType.EXHIBITOR
              ? 'An'
              : 'A'
          } ${subject.type} subject with this email already exists.`
        )
        return false
      } else {
        await FirestoreDatabase.collection(Collection.SUBJECTS)
          .doc(subjectId)
          .update({
            ...payload,
            conferenceId
          })

        if (subject?.type === SubjectType.SPEAKER && subject?.speakerId) {
          const subjectPayload = { ...payload }
          subjectPayload?.token && delete subjectPayload['token']
          subjectPayload?.type && delete subjectPayload['type']
          subjectPayload?.status && delete subjectPayload['status']
          await FirestoreDatabase.collection(Collection.SPEAKERS)
            .doc(subject?.speakerId)
            .update({
              ...subjectPayload
            })
        }

        showSuccessMsg && toast.success('Subject updated successfully.')
        return true
      }
    } catch (err) {
      dispatch(subjectSlice.actions.setError(err))
    }
  }

export const submitSubjectData = (subjectId, payload) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.SUBJECTS)
      .doc(subjectId)
      .update({
        ...payload
      })
    dispatch(subjectSlice.actions.setSubject(payload))
    return true
  } catch (err) {
    console.error('Error: ', err)
  }
}

export const updateSubjectStatus = (subjectId, status) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.SUBJECTS).doc(subjectId).update({
      status: status
    })
    dispatch(subjectSlice.actions.setSubjectStatus(status))
    return true
  } catch (err) {
    dispatch(subjectSlice.actions.setError(err))
  }
}

export const deleteSubjectById = async (subjectId) => {
  try {
    const subjectDoc = await FirestoreDatabase.collection(Collection.SUBJECTS).doc(subjectId).get()
    const subject = { ...subjectDoc.data(), id: subjectDoc.id }
    if (subject.type === SubjectType.EXHIBITOR && subject?.logoImage) {
      await deleteImageFromStorageIfExist(`images/subjects/${subject?.logoImage}`)
    } else if (subject.type === SubjectType.SPEAKER && subject?.imageName) {
      await deleteImageFromStorageIfExist(`images/subjects/${subject?.imageName}`)
    } else {
      await Promise.all([
        subject?.adImageName &&
          deleteImageFromStorageIfExist(`images/subjects/${subject?.adImageName}`),
        subject?.logoImageName &&
          deleteImageFromStorageIfExist(`images/subjects/${subject?.logoImageName}`),
        subject?.brochureImageName &&
          deleteImageFromStorageIfExist(`images/subjects/${subject?.brochureImageName}`)
      ])
    }
    if (subject?.type === SubjectType.SPEAKER) {
      const subjectTalks = await FirestoreDatabase.collection(Collection.TALKS)
        .where('subjectId', '==', subject?.id)
        .get()
      const talks = subjectTalks.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
      const attachedPollsWithTalks = talks?.map((talk) => talk?.pollId)
      const pollsQuestions = []
      for (const pollId of attachedPollsWithTalks) {
        const snapshot = await FirestoreDatabase.collection(Collection.POLLS)
          .doc(pollId)
          .collection(Collection.QUESTIONS)
          .get()
        snapshot.docs.forEach((doc) => {
          pollsQuestions.push({ id: doc?.id, ...doc.data(), pollId: pollId })
        })
      }
      const pollsQuestionsRefs = pollsQuestions?.map((question) =>
        FirestoreDatabase.collection(Collection.POLLS)
          .doc(question.pollId)
          .collection(Collection.QUESTIONS)
          .doc(question.id)
      )
      const pollRefs = attachedPollsWithTalks.map((pollId) =>
        FirestoreDatabase.collection(Collection.POLLS).doc(pollId)
      )
      const talkRefs = talks?.map((talk) =>
        FirestoreDatabase.collection(Collection.TALKS).doc(talk.id)
      )
      await Promise.all([
        deleteBatchDocuments(pollsQuestionsRefs),
        deleteBatchDocuments(pollRefs),
        deleteBatchDocuments(talkRefs)
      ])
    }

    if (
      subject?.type === SubjectType.SPEAKER &&
      subject?.speakerId &&
      subject?.status !== SubjectStatus.APPROVED
    ) {
      await FirestoreDatabase.collection(Collection.SPEAKERS).doc(subject.speakerId).delete()
    }

    if (subject.status === SubjectStatus.APPROVED) {
      if (subject.type === SubjectType.EXHIBITOR && subject?.vendorId) {
        await FirestoreDatabase.collection(Collection.VENDORS).doc(subject.vendorId).delete()
      } else if (subject.type === SubjectType.SPEAKER && subject?.speakerId) {
        await FirestoreDatabase.collection(Collection.SPEAKERS).doc(subject.speakerId).delete()
      } else if (subject.type === SubjectType.SPONSOR && subject?.sponsorId) {
        await FirestoreDatabase.collection(Collection.SPONSORS).doc(subject.sponsorId).delete()
      }
    }
    await FirestoreDatabase.collection(Collection.SUBJECTS).doc(subjectId).delete()
    toast.success('Subject deleted successfully.')
    return true
  } catch (err) {
    console.error(err)
  }
}

export const sendInviteEmailToSubjects = async (subjectType, subjectStatus, conferenceId) => {
  try {
    const response = await cloudFunctionsInstance.post('/inviteOrRemindSubjectsViaEmail', {
      subjectType,
      subjectStatus,
      conferenceId
    })
    const data = response.data
    if (data.successCount > 0) {
      toast.success(`${data.successCount} Email(s) sent successfully.`)
    }
    if (data.failedCount > 0) {
      toast.error(`${data.failedCount} Email(s) failed to send.`)
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send email(s), Please try again later'
    toast.error(errorMessage)
  }
}

export const sendInviteOrReminderEmailToAttendees = async (subjectStatus, conferenceId) => {
  try {
    await cloudFunctionsInstance.post('/inviteOrRemindAttendeeSubjectsViaEmail', {
      subjectStatus,
      conferenceId
    })
    toast.success('Email(s) sent successfully.')
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send email(s), Please try again later'
    toast.error(errorMessage)
  }
}

export const sendInviteEmailToSpecificSubject = async (subjectId, conferenceId) => {
  try {
    await cloudFunctionsInstance.post('/inviteOrRemindOneSubjectViaEmail', {
      subjectId,
      conferenceId
    })
    toast.success(`Email sent successfully.`)
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send email invite. Please try again later.'
    toast.error(errorMessage)
  }
}

export const sendConfirmEmailToSubject = async (subjectId) => {
  try {
    await cloudFunctionsInstance.post('/confirmationEmailToSubject', {
      subjectId
    })
    toast.success('Confirmation email sent successfully.')
  } catch (err) {
    const errorMessage = err?.response?.data?.msg || 'Failed to send confirmation email.'
    toast.error(errorMessage)
  }
}

export const sendBatchRemindersToSpeakersForTalks = async (subjectIds) => {
  try {
    await cloudFunctionsInstance.post('/remindSpeakersToSubmitTalks', {
      subjectIds
    })
    toast.success('Reminder Email(s) sent successfully.')
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send reminder emails. Please try again later.'
    toast.error(errorMessage)
  }
}

export const notifySubjectsToUpdateInformation = async (subjectType, conferenceId) => {
  try {
    const response = await cloudFunctionsInstance.post('/emailSubjectsToUpdateInfo', {
      subjectType,
      conferenceId
    })
    const data = response.data
    if (data.successCount > 0) {
      toast.success(`${data.successCount} Email(s) sent successfully.`)
    }
    if (data.failedCount > 0) {
      toast.error(`${data.failedCount} Email(s) failed to send.`)
    }
  } catch (err) {
    const errorMessage =
      err?.response?.data?.msg || 'Failed to send email(s), Please try again later'
    toast.error(errorMessage)
  }
}

export const verifyGoogleRecaptcha = (userResponse) => async (dispatch) => {
  try {
    const response = await cloudFunctionsInstance.post('/verifyRecaptchaResponse', {
      userResponse
    })
    return response.data.isCaptchaVerified
  } catch (err) {
    dispatch(subjectSlice.actions.setError(err))
  }
}

export const verifyConferenceExpiryDate = (conferenceId) => async (dispatch) => {
  try {
    dispatch(subjectSlice.actions.setProgress(true))
    const response = await cloudFunctionsInstance.get(
      `/getConferenceById?conferenceId=${conferenceId}`
    )
    dispatch(conferenceSlice.actions.setConference(response.data))
  } catch (err) {
    dispatch(subjectSlice.actions.setError(err))
  } finally {
    dispatch(subjectSlice.actions.setProgress(false))
  }
}

export const getSubjects = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.SUBJECTS)
    .where('conferenceId', '==', conferenceId)
    .onSnapshot((documents) => {
      const subjects = []
      documents.forEach((doc) => {
        subjects.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(subjects)
    })
}

export const resetSubject = () => (dispatch) => {
  dispatch(subjectSlice.actions.setSubject(null))
}

export default subjectSlice
